import * as THREE from "three";

// Static batching (#89): scenery that never moves after it's built draws as
// one mesh per material and shadow setup instead of one per part. Parts are
// baked into the root's space (positions by their matrix, normals by its
// normal matrix, as `applyMatrix4` does) in one pass, and an
// ink outline comes back as an `outline` child of the merged mesh, pushing
// along the merged normals: the same thing when nothing is scaled.

/**
 * Returns `root`'s meshes merged into as few meshes as keep what draws them
 * the same: one per material (by identity, so share materials to batch),
 * shadow flags, render order and outline material. The source geometries
 * are disposed; the materials are reused. The result stands where `root`
 * did.
 */
export function mergeStatic(root: THREE.Object3D): THREE.Group {
  root.updateMatrixWorld(true);
  const toRoot = root.matrixWorld.clone().invert();
  const buckets = new Map<string, { sample: THREE.Mesh; outline: THREE.Mesh | undefined; parts: Part[] }>();
  const sources = new Set<THREE.BufferGeometry>();
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh) || o.name === "outline") return;
    if (Array.isArray(o.material)) throw new Error("mergeStatic: material arrays aren't supported");
    const outline = o.children.find((c): c is THREE.Mesh => c instanceof THREE.Mesh && c.name === "outline");
    if (outline && outline.geometry !== o.geometry) throw new Error("mergeStatic: an outline must share its mesh's geometry");
    const key = [o.material.uuid, o.castShadow, o.receiveShadow, o.renderOrder, outline ? (outline.material as THREE.Material).uuid : "-"].join(" ");
    const bucket = buckets.get(key) ?? { sample: o, outline, parts: [] as Part[] };
    bucket.parts.push({ geometry: o.geometry as THREE.BufferGeometry, matrix: new THREE.Matrix4().multiplyMatrices(toRoot, o.matrixWorld) });
    buckets.set(key, bucket);
    sources.add(o.geometry as THREE.BufferGeometry);
  });
  const group = new THREE.Group();
  group.position.copy(root.position);
  group.quaternion.copy(root.quaternion);
  group.scale.copy(root.scale);
  for (const { sample, outline, parts } of buckets.values()) {
    const geometry = mergeParts(parts);
    const mesh = new THREE.Mesh(geometry, sample.material);
    mesh.castShadow = sample.castShadow;
    mesh.receiveShadow = sample.receiveShadow;
    mesh.renderOrder = sample.renderOrder;
    if (outline) {
      const ink = new THREE.Mesh(geometry, outline.material);
      ink.name = "outline";
      ink.castShadow = outline.castShadow;
      ink.receiveShadow = outline.receiveShadow;
      mesh.add(ink);
    }
    group.add(mesh);
  }
  for (const g of sources) g.dispose();
  return group;
}

interface Part {
  geometry: THREE.BufferGeometry;
  matrix: THREE.Matrix4;
}

/** One indexed geometry holding every part, each baked through its matrix. */
function mergeParts(parts: Part[]): THREE.BufferGeometry {
  const names = Object.keys(parts[0]!.geometry.attributes).sort();
  let vertices = 0;
  let indices = 0;
  for (const { geometry } of parts) {
    if (Object.keys(geometry.attributes).sort().join() !== names.join()) throw new Error("mergeStatic: parts with different attributes can't merge");
    const count = geometry.getAttribute("position").count;
    vertices += count;
    indices += geometry.index ? geometry.index.count : count;
  }
  const merged = new THREE.BufferGeometry();
  const out: Record<string, Float32Array> = {};
  for (const name of names) {
    const size = parts[0]!.geometry.getAttribute(name).itemSize;
    out[name] = new Float32Array(vertices * size);
  }
  const index = vertices > 65535 ? new Uint32Array(indices) : new Uint16Array(indices);
  const normalMatrix = new THREE.Matrix3();
  const v = new THREE.Vector3();
  let base = 0;
  let at = 0;
  for (const { geometry, matrix } of parts) {
    normalMatrix.getNormalMatrix(matrix);
    const count = geometry.getAttribute("position").count;
    for (const name of names) {
      const src = geometry.getAttribute(name) as THREE.BufferAttribute;
      const dst = out[name]!;
      const size = src.itemSize;
      for (let i = 0; i < count; i++) {
        if (name === "position" || name === "normal") {
          v.fromBufferAttribute(src, i);
          if (name === "position") v.applyMatrix4(matrix);
          else v.applyMatrix3(normalMatrix).normalize();
          dst[(base + i) * 3] = v.x;
          dst[(base + i) * 3 + 1] = v.y;
          dst[(base + i) * 3 + 2] = v.z;
        } else {
          for (let c = 0; c < size; c++) dst[(base + i) * size + c] = src.getComponent(i, c);
        }
      }
    }
    const src = geometry.index;
    const n = src ? src.count : count;
    for (let k = 0; k < n; k++) index[at + k] = base + (src ? src.getX(k) : k);
    base += count;
    at += n;
  }
  for (const name of names) merged.setAttribute(name, new THREE.BufferAttribute(out[name]!, parts[0]!.geometry.getAttribute(name).itemSize));
  merged.setIndex(new THREE.BufferAttribute(index, 1));
  return merged;
}
